import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';
import { AppName } from '../../../data-models/integrations-app.data-model';
import { DetailsConfigActions } from '../../AdminPanel/DataDisplay/DetailsConfigActions';
import { ExitConfigBlocker } from '../../AdminPanel/DataDisplay/ExitConfigBlocker';
import { ConnectionDetailsSection } from '../components/ConnectionDetailsSection';
import { IntegrationDetailHeader } from '../components/IntegrationDetailHeader';
import { LabeledTextField } from '../components/LabeledTextField';
import { SettingsCard } from '../components/SettingsCard';
import { CobaltFormValues } from './cobaltConfig.schema';
import { useCobaltConfigFormState } from './useCobaltConfigFormState';

export function CobaltConfiguration() {
  const {
    editStatus,
    isConnected,
    apiKeyLast4,
    isLoading,
    successMessage,
    errorMessage,
    methods,
    onSave,
    onReset,
  } = useCobaltConfigFormState();

  const description =
    'Configure your Cobalt API key. Metrics are pulled nightly into staging and loaded into KPIs through Field Automation.';

  if (isLoading) {
    return (
      <Stack alignItems='center' justifyContent='center' minHeight='400px'>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <FormProvider {...methods}>
      <Stack width='100%' height='100%' p='12px' gap='1rem' overflow='auto'>
        <ExitConfigBlocker onSave={onSave} editStatus={editStatus} />
        <IntegrationDetailHeader appName={AppName.Cobalt} description={description} />

        {successMessage && <Alert severity='success'>{successMessage}</Alert>}
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

        <SettingsCard>
          <ConnectionDetailsSection isConnected={isConnected} />

          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <ApiKeyField isConnected={isConnected} apiKeyLast4={apiKeyLast4} />
          </Box>

          <Stack direction='row' gap='1rem' alignItems='center'>
            <DetailsConfigActions
              save={{ handler: onSave, disabled: editStatus !== 'modified' }}
              reset={{ handler: onReset }}
            />
          </Stack>
        </SettingsCard>
      </Stack>
    </FormProvider>
  );
}

function ApiKeyField({ isConnected, apiKeyLast4 }: { isConnected: boolean; apiKeyLast4: string | null }) {
  const {
    control,
    formState: { errors },
  } = useFormContext<CobaltFormValues>();

  return (
    <Stack gap='0.5rem'>
      {isConnected && apiKeyLast4 && (
        <Typography variant='body2' color='text.secondary'>
          A key ending in {apiKeyLast4} is stored.
        </Typography>
      )}
      <Controller
        name='apiKey'
        control={control}
        render={({ field }) => (
          <LabeledTextField
            {...field}
            label='API Key'
            description={
              isConnected
                ? 'Leave blank to keep the existing key. Enter a new value to replace it.'
                : 'Securely stored.'
            }
            required={!isConnected}
            type='password'
            autoComplete='new-password'
            placeholder={isConnected ? 'Leave blank to keep the existing key' : 'Enter your Cobalt API key'}
            error={!!errors.apiKey}
            helperText={errors.apiKey?.message}
          />
        )}
      />
    </Stack>
  );
}
